import { IFileResponse } from '@file/projectFile/projectFile.models';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { downloadAttachmentAsync } from '@file/file.services';
import { AxiosErrorResponse } from '@core/error.models';
import { IDownloadAttachmentRequest } from '@file/file.models';
import { ComponentType, Grid, Icon, Loader, blobToBase64Async } from '@gcl/react-core';
import classnames from 'classnames';
import { IGalleryItem } from './gallery.models';

interface IGalleryImage {
  item: IGalleryItem;
  index: number;
  thumbnailWidth: number;
  onClickThumbnail: (index: number) => void;
  updateSlide: (index: number, source: string, mimeType: string | undefined) => void;
  shouldShowFirstThumbnail: boolean;
  isVisibleInViewport?: boolean;
}
export const GalleryImage = ({
  item,
  index,
  thumbnailWidth,
  onClickThumbnail,
  updateSlide,
  shouldShowFirstThumbnail,
  isVisibleInViewport = false,
}: IGalleryImage) => {
  const [base64Source, setBase64Source] = useState<string>();

  const isYoutube = item.imageSource.includes('youtube');

  const {
    error: errorDownloadFile,
    isLoading: isLoadingDownloadFile,
    mutate: mutateDownloadFile,
    data: dataDownloadFile,
  } = useMutation<IFileResponse, AxiosErrorResponse, IDownloadAttachmentRequest>(downloadAttachmentAsync, {});

  useEffect(() => {
    if (!isYoutube)
      mutateDownloadFile({
        id: item.itemId,
        blobUrl: item.imageSource,
      });
  }, [JSON.stringify(item), isYoutube]);

  useEffect(() => {
    if (!dataDownloadFile?.file) return;

    blobToBase64Async(dataDownloadFile.file).then((base64) => {
      setBase64Source(base64 as string);
    });
  }, [JSON.stringify(dataDownloadFile)]);

  useEffect(() => {
    if (base64Source) updateSlide(index, base64Source, dataDownloadFile?.mimeType);
  }, [base64Source]);

  const contentType = useMemo(() => {
    if (isLoadingDownloadFile || !dataDownloadFile) return 'icon';
    if (dataDownloadFile?.mimeType?.startsWith('video')) return 'video';
    return 'image';
  }, [isLoadingDownloadFile, JSON.stringify(dataDownloadFile), base64Source, onClickThumbnail]);

  const content = useMemo(() => {
    if (isLoadingDownloadFile) return <Loader type={ComponentType.Primary} text="" />;

    if (isYoutube)
      return (
        // eslint-disable-next-line jsx-a11y/iframe-has-title
        <iframe src={item.imageSource} width="100%" allowFullScreen style={{ height: '-webkit-fill-available' }} />
      );

    if (!dataDownloadFile) return <Icon iconName="ExclamationCircle" />;

    if (dataDownloadFile?.mimeType?.startsWith('video'))
      return (
        // eslint-disable-next-line jsx-a11y/media-has-caption, jsx-a11y/mouse-events-have-key-events
        <video src={base64Source} />
      );
    return <img alt="" src={base64Source} />;
  }, [isLoadingDownloadFile, JSON.stringify(dataDownloadFile), base64Source]);

  return (
    <>
      {index === 0 && (
        <Grid item xs={12} sm={12} md={12}>
          <div
            className={classnames('gcl-gems-gallery-item', {
              'gcl-gems-gallery-item-with-icon': contentType === 'icon',
              'gcl-gems-gallery-item-image-enlarged': contentType === 'image',
              'gcl-gems-gallery-item-video-enlarged': contentType === 'video',
            })}
            onClick={() => onClickThumbnail(index)}
          >
            {content}
          </div>
        </Grid>
      )}
      {(index > 0 || shouldShowFirstThumbnail) && (
        <Grid item xs={6} md={4}>
          <div
            className={classnames('gcl-gems-gallery-item', {
              'gcl-gems-gallery-item-with-icon': contentType === 'icon',
              'gcl-gems-gallery-item-image': contentType === 'image',
              'gcl-gems-gallery-item-video': contentType === 'video',
            })}
            onClick={() => onClickThumbnail(index)}
          >
            {content}
          </div>
        </Grid>
      )}
    </>
  );
};
